import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTheme } from "../../hooks/useTheme";
import { ThemeContext } from "../../contexts/theme";
import { ExchangeRateProvider } from "../../contexts/exchangeRate";
import { useAuth } from "../../services/useAuth";

import AppContent from "../../components/CustomUI/AppContent";
import AppFooter from "../../components/CustomUI/AppFooter";
import AppHeader from "../../components/CustomUI/AppHeader";
import Sidebar from "../../components/CustomUI/Sidebar";
import Loading from "../../components/CustomUI/Loading";

import styles from "./Default.module.scss";

const Default = () => {
  const theme = useTheme();
  const { sidebarTheme, topMenuTheme } = useContext(ThemeContext);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [appContentClass, setAppContentClass] = useState("");
  const [openMenuMobile, setOpenMenuMobile] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);

  const location = useLocation();

  // Check authentication
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1199) {
        setOpenMenuMobile(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const routeName = location.pathname + location.hash;

    if (
      (routeName.startsWith("/emails") || routeName.startsWith("/chat")) &&
      window.innerWidth > 767.87
    ) {
      setHideFooter(true);
      setAppContentClass("app-content-container--reset-padding");
    } else {
      setHideFooter(false);
      setAppContentClass("");
    }

    if (
      (routeName.startsWith("/emails") || routeName.startsWith("/chat")) &&
      window.innerWidth < 767.88
    ) {
      setAppContentClass("app-content-container--reset-padding-mobile");
      setHideFooter(true);
    }
  }, [location.pathname, location.hash]);

  if (loading) {
    return (
      <div
        className={`${styles["default-shell"]} ${styles[theme]}`}
        m-data-theme={theme}
        m-data-sidebar-theme={sidebarTheme}
        m-data-top-menu-theme={topMenuTheme}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Loading />
      </div>
    );
  }

  // Don't render if not authenticated
  if (!user) {
    return null;
  }

  return (
    <ExchangeRateProvider>
      <div
        className={`${styles["default-container"]} ${styles[theme]}`}
        m-data-theme={theme}
        m-data-sidebar-theme={sidebarTheme}
        m-data-top-menu-theme={topMenuTheme}
      >
        <aside
          className={`${styles["default-sidebar"]} ${
            openMenuMobile ? styles["default-sidebar--open"] : ""
          } ${styles[theme]}`}
          aria-label="Main navigation"
        >
          <Sidebar
            toggledMenu
            menuMobile
            useBorderRight={hideFooter}
            onClose={({ hasChildrens } = {}) => {
              if (!hasChildrens) setOpenMenuMobile(false);
            }}
            toggleMenu={() => setOpenMenuMobile(false)}
          />
        </aside>

        <main className={styles["default-main"]} id="default-main">
          <div className={styles["default-main__column"]}>
            <AppHeader
              toggleMenuMobile={() => {
                setOpenMenuMobile(!openMenuMobile);
              }}
              useBorderBottom={hideFooter}
            />

            <AppContent className={appContentClass} />
          </div>
          {!hideFooter && (
            <div className={styles["default-main__footer"]}>
              <AppFooter />
            </div>
          )}
        </main>
        {openMenuMobile && (
          <button
            type="button"
            className={styles["overlay-container"]}
            onClick={() => setOpenMenuMobile(false)}
            aria-label="Menüyü kapat"
          />
        )}
      </div>
    </ExchangeRateProvider>
  );
};

export default Default;
